import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import { Card, Spinner, ErrorMessage } from '../components/ui'

// The shape of the deal, without recomputing an evaluation.
//
// Two teams read as a swap ("Gibbs, Pickens ⇄ Chase"), because that is what it
// is. Three or more cannot: ⇄ claims the players either side of it changed
// hands with each other, which in a ring is false, and grouping by giver alone
// throws away the direction that makes a multi-team trade legible at all. So
// past two, each group says where it is going.
function TradeShape({ assets }) {
  const groups = new Map()

  assets.forEach((asset) => {
    const key = `${asset.from_team_id}->${asset.to_team_id}`
    if (!groups.has(key)) {
      groups.set(key, { to: asset.to_team_name, players: [] })
    }
    groups.get(key).players.push(asset.player_name)
  })

  const legs = [...groups.values()]
  if (legs.length === 0) return null

  const teams = new Set(assets.flatMap((a) => [ a.from_team_id, a.to_team_id ]))
  const swap = teams.size <= 2

  return (
    <p className="mt-0.5 truncate text-xs text-slate-400">
      {legs.map((leg, index) => (
        <span key={index}>
          {index > 0 && (
            <span className="mx-1.5 text-slate-400">{swap ? '⇄' : '·'}</span>
          )}
          {leg.players.join(', ')}
          {!swap && leg.to && (
            <span className="text-slate-500"> → {leg.to}</span>
          )}
        </span>
      ))}
    </p>
  )
}

export default function TradesList() {
  const trades = useQuery({
    queryKey: ['trades'],
    queryFn: () => api.trades(),
  })

  return (
    <Layout>
      <h1 className="mb-4 text-lg font-semibold text-slate-100">
        Saved trades
      </h1>

      {trades.isPending && <Spinner />}
      <ErrorMessage error={trades.error} />

      {trades.data && trades.data.trades.length === 0 && (
        <Card title="Nothing saved yet">
          <p className="text-sm text-slate-300">
            Evaluate a trade, then give it a name to keep it here. Unnamed
            evaluations are treated as scratch work and cleared out after a week.
          </p>
        </Card>
      )}

      <div className="space-y-2">
        {(trades.data?.trades || []).map((trade) => (
          <Link
            key={trade.id}
            to={`/trades/${trade.id}`}
            className="block rounded-lg px-4 py-3 border border-slate-700 bg-slate-900 hover:border-slate-500"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="min-w-0 truncate font-medium text-slate-100">
                  {trade.name}
                </span>
                {trade.exploratory && (
                  <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide border border-slate-600 text-slate-400">
                    Exploration
                  </span>
                )}
              </span>
              <span className="shrink-0 text-xs text-slate-500">
                {new Date(trade.created_at).toLocaleDateString()}
              </span>
            </div>

            <p className="truncate text-xs text-slate-400">
              {trade.league_name}
            </p>

            <TradeShape assets={trade.assets} />
          </Link>
        ))}
      </div>
    </Layout>
  )
}
